import {
  Button,
  Center,
  ErrorMessage as ErrorMessageComponent,
  IconCircleQuestionmark,
  IndigoIndigo60,
  Link
} from '@sequencehq/core-components'
import { ShareFeedbackModal } from 'components/ShareFeedbackModal/ShareFeedbackModal'
import { FC, ReactNode, useMemo, useState } from 'react'
import { match } from 'ts-pattern'

type ErrorMessageProps = {
  errorType: 'notFound' | 'general' | 'notAuthorized'
  graphic?: ReactNode
  canSubmitFeedback?: boolean
}

export const ErrorMessage: FC<ErrorMessageProps> = ({
  errorType,
  graphic,
  canSubmitFeedback = true
}) => {
  const [showShareFeedbackModal, setShowShareFeedbackModal] = useState(false)

  const title = useMemo(() => {
    return match(errorType)
      .with('notFound', () => 'Oops! Page not found')
      .with('general', () => undefined)
      .with('notAuthorized', () => 'You need access')
      .exhaustive()
  }, [errorType])

  const message = useMemo(() => {
    return match(errorType)
      .with('notFound', () => (
        <>
          We can't find the page you're looking for. Try going back to{' '}
          <Link
            to={'/home'}
            color={IndigoIndigo60}
            _hover={{ textDecoration: 'none' }}
          >
            home
          </Link>
          .
        </>
      ))
      .with(
        'general',
        () =>
          'Try reloading the page if you believe this is an error, or send us feedback.'
      )
      .with('notAuthorized', () => (
        <>
          Request access, or switch to an account with access.{' '}
          <Link.Unstyled
            style={{ textDecoration: 'none', color: IndigoIndigo60 }}
            to="https://docs.sequencehq.com/roles-permissions/overview"
            target="_blank"
          >
            Learn more.
          </Link.Unstyled>
        </>
      ))
      .exhaustive()
  }, [errorType])

  return (
    <>
      <Center width="100%" height="100%" padding="20px">
        <ErrorMessageComponent
          graphic={graphic}
          data-testid={`errorPage.${errorType}`}
          title={title}
          message={message}
          button={
            canSubmitFeedback ? (
              <Button
                variant="secondary"
                onClick={() => setShowShareFeedbackModal(true)}
                leadingIcon={<IconCircleQuestionmark />}
              >
                Submit feedback
              </Button>
            ) : undefined
          }
        />
      </Center>

      {showShareFeedbackModal && (
        <ShareFeedbackModal onClose={() => setShowShareFeedbackModal(false)} />
      )}
    </>
  )
}
